import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { batch } from 'react-redux'
import { fetchPages, fetchPage, createPage, updatePage, destroyPage } from './pagesAPI'
import {
  selectAuthToken
} from '../user/userSlice'
import {
  addStiki,
  loadStikisData,
  loadStikisForPage,
  selectAllStikisOnPage,
  removeAllStikisForPage,
  selectMinStikiId,
  selectMaxStikiZIndex,
} from '../stikis/stikisSlice'
import {
  selectIsStatic,
  setIsStatic,
  setFlashNotice,
  setFlashAlert,
} from '../ui/uiSlice'
import { navigateTo } from '../../../router/navigation'

import {
  PAGE_DEFAULT_TITLE,
  PAGE_DEFAULT_LEFT_EDGE,
  PAGE_DEFAULT_TOP_EDGE,
  PAGE_DEFAULT_ZOOM,
  PAGE_ZOOM_PRESETS,
  PAGE_BASE_FONT_SIZE,
} from '../../constants'

const initialState = {
  isFetching: false,
  didValidate: false,
  lastUpdated: null,
  allIds: [],
  byIds: {},
  // 0.6.27.21: which pages' stikis have actually been fetched this session
  // (`fetchPageStikisAsync`), so opening a page already seen doesn't
  // refetch it.
  stikisLoadedIds: [],
}

const EMPTY_PAGE_IDS = []
const EMPTY_PAGE_MAP = {}

export const loadStaticDataAsync = (json) => async (dispatch, getState) => {
  batch(() => {
    dispatch(setIsStatic(json.isStatic))
    dispatch(loadStikisData(json))
    dispatch(loadPagesData(json))
    dispatch(setFlashNotice(json.flash.notice))
    dispatch(setFlashAlert(json.flash.alert))
  })
}

export const fetchPagesAsync = () => async (dispatch, getState) => {
  console.log("state before pages fetch:", getState().user)
  const json = await fetchPages(dispatch)
  console.log("json:", json)
  // 0.6.27.21: /pages.json no longer carries any page's stikis, so there is
  // nothing here for loadStikisData to load any more -- it would only wipe
  // stikisSlice back to empty, racing fetchPageStikisAsync's per-page loads.
  batch(() => {
    dispatch(setIsStatic(false))
    dispatch(loadPagesData(json))
  })
}

// 0.6.27.21: /pages.json no longer embeds any page's stikis -- this fetches
// one page's, from GET /pages/:id.json, the first time that page is opened.
// Guarded by `selectPageStikisLoaded` so re-opening a page already seen this
// session doesn't refetch it, and skipped entirely for the logged-out demo,
// which already has every page's stikis loaded up front.
export const fetchPageStikisAsync = (pageId) => async (dispatch, getState) => {
  const state = getState()

  if (selectIsStatic(state) || selectPageStikisLoaded(pageId)(state)) {
    return
  }

  const json = await fetchPage(pageId, dispatch)
  if (!json?.page) {
    // Mark it loaded (attempted) regardless, so a loading indicator tied to
    // selectPageStikisLoaded doesn't spin forever on a failed request.
    dispatch(setPageStikisLoaded(pageId))
    return
  }

  const { stikis, ...page } = json.page

  batch(() => {
    dispatch(loadStikisForPage({ pageId, stikis: stikis || [] }))
    dispatch(mergePageData({ pageId, page }))
    dispatch(setPageStikisLoaded(pageId))
  })
}

export const createPageAsync = (data) => async (dispatch, getState) => {

  console.log('createPageAsync')

  const state = getState()
  data.authenticity_token = selectAuthToken(state)

  console.log('page data', data)

  const json = await (async () => {
    if (selectIsStatic(state)) {
      const id = selectMinPageId(state) - 1
      const title = selectNextAvailableTitle(data.title || "New page")(state)
      return {
        page: {
          id,
          title,
          left_edge: 0,
          top_edge: 0,
          zoom: 1,
          user_id: -1,
          stikis: [{
            id: selectMinStikiId(state) - 1,
            z_index: selectMaxStikiZIndex(state) + 1,
            text: `<b>${title}</b>`,
            left_edge: -7.5,
            top_edge: -8,
            width: 15,
            height: 7,
            color: "#F0E68C",
            page_id: id,
          }],
        }
      }
    } else {
      return await createPage(data, dispatch)
    }
  })()

  // console.log('page json', json, 'json.page', json.page, 'json.page.stikis', json.page.stikis)

  let stikis
  try {
    stikis = json.page.stikis
  } catch (error) {
    return
  }

  batch(() => {
    stikis.forEach((e) => {
      // console.log('stiki', e)
      dispatch(addStiki(e))
    })
    dispatch(addPage(json))
    navigateTo(`/app/pages/${json.page.id}`)
  })
}

export const zoomByPageAsync = (data) => async (dispatch, getState) => {
  const pageId = data.pageId
  const zoomBy = data.zoomBy
  const position = data.position

  batch(() => {
    dispatch(addPositionToPage({ pageId, position }))
    dispatch(zoomByPage({ pageId, zoomBy }))
  })
  dispatch(updatePageAsync(pageId))
}

export const updatePageAsync = (pageId) => async (dispatch, getState) => {
  const state = getState()

  // A page someone shared with me is read-only, but zooming and panning are how
  // you read it: without them a page can be unreadable. So the view still moves
  // locally and nothing is sent. The server would refuse anyway, and the saved
  // position belongs to the owner, not to whoever is looking at it.
  //
  // Every zoom and pan goes through here — the zoom buttons, the wheel, dragging
  // the background, the arrow keys, `zoomByPageAsync` — so this one gate covers
  // the lot.
  if (!selectPageIsMine(pageId)(state)) {
    return
  }

  const authenticity_token = selectAuthToken(state)
  const page = selectPage(pageId)(state)

  const updateData = {
    page,
    authenticity_token,
  }

  const json = await (async () => {
    if (selectIsStatic(state)) {
      return { page: {} }
    } else {
      return await updatePage(pageId, updateData, dispatch)
    }
  })()

  let pageData
  try {
    pageData = json.page
  } catch (error) {
    return
  }

  // TODO update the state for e.g. whitelisted title?
}

export const destroyPageAsync = (pageId) => async (dispatch, getState) => {
  const state = getState()
  const pageToDelete = state.pages.byIds[pageId]
  const stikisToDelete = selectAllStikisOnPage(pageId)(state)

  const destroyData = {
    authenticity_token: selectAuthToken(state),
  }

  // Optimistically remove the page and all associated stikis.
  // TODO: Roll back if the request fails
  batch(() => {
    dispatch(removePage(pageId))
    dispatch(removeAllStikisForPage(pageId))
  })

  const json = await (async () => {
    if (selectIsStatic(state)) {
      return {}
    } else {
      return await destroyPage(pageId, destroyData, dispatch)
    }
  })()
}

export const pagesSlice = createSlice({
  name: 'pages',
  initialState,
  reducers: {
    addPage: (state, action) => {
      const sourcePage = action.payload.page
      const { stikis, ...page } = sourcePage
      const pageId = page.id
      state.allIds.unshift(pageId)
      state.byIds[pageId] = page
      // A new page's stiki is added separately (see createPageAsync), so
      // there is nothing left for fetchPageStikisAsync to go and get.
      state.stikisLoadedIds.push(pageId)
    },
    loadPagesData: (state, action) => {
      // const byIds = {}
      const pages = action.payload.pages
      state.byIds = {}
      state.allIds = pages.map((e) => {
        const sourcePage = e.page
        const { stikis, ...page } = sourcePage
        state.byIds[page.id] = page
        return page.id
      })
      state.isFetching = false
      state.hasLoaded = true
    },
    zoomInPage: (state, action) => {
      const pageId = action.payload
      const page = state.byIds[pageId]
      page.zoom =
        PAGE_ZOOM_PRESETS.filter((e) => e > page.zoom).sort()[0] || page.zoom
    },
    zoomOutPage: (state, action) => {
      const pageId = action.payload
      const page = state.byIds[pageId]
      page.zoom =
        PAGE_ZOOM_PRESETS.filter((e) => e < page.zoom)
          .sort()
          .reverse()[0] || page.zoom
    },
    zoomByPage: (state, action) => {
      const pageId = action.payload.pageId
      const zoomBy = action.payload.zoomBy
      state.byIds[pageId].zoom += zoomBy
    },
    addPositionToPage: (state, action) => {
      const pageId = action.payload.pageId
      const position = action.payload.position

      state.byIds[pageId].left_edge += position.x
      state.byIds[pageId].top_edge += position.y
    },
    setPageTitle: (state, action) => {
      const pageId = action.payload.pageId
      const title = action.payload.title

      state.byIds[pageId].title = title
    },
    removePage: (state, action) => {
      const pageId = action.payload
      const idx = state.allIds.indexOf(pageId)
      state.allIds.splice(idx, 1)
      delete state.byIds[pageId]
    },
    // Merges a freshly-fetched page's own fields (title, position, zoom, ...)
    // over whatever the page list already held for it -- see
    // fetchPageStikisAsync. Not a replace: the page is already in byIds from
    // the list fetch, and this just brings it up to date.
    mergePageData: (state, action) => {
      const { pageId, page } = action.payload
      state.byIds[pageId] = { ...state.byIds[pageId], ...page }
    },
    setPageStikisLoaded: (state, action) => {
      const pageId = action.payload
      if (!state.stikisLoadedIds.includes(pageId)) {
        state.stikisLoadedIds.push(pageId)
      }
    },
    clearPagesData: () => ({
      ...initialState,
      allIds: null,
      byIds: null,
    }),
  },
  extraReducers: (builder) => { },
})

export const {
  addPage,
  zoomInPage,
  zoomOutPage,
  zoomByPage,
  loadPagesData,
  addStikiToPage,
  addPositionToPage,
  setPageTitle,
  removePage,
  mergePageData,
  setPageStikisLoaded,
  clearPagesData,
} = pagesSlice.actions

export default pagesSlice.reducer

export const selectPage = (id) => (state) => state.pages?.byIds?.[id]

export const selectPageZoom = (id) => (state) => state.pages?.byIds?.[id]?.zoom

export const selectPageTitle = (id) => (state) => state.pages?.byIds?.[id]?.title

export const selectAllPages = (state) => state.pages?.allIds || EMPTY_PAGE_IDS

// Whose page it is, as the server says (`is_mine` in the page JSON). Only an
// explicit false means someone else's: the fabricated data for the logged-out
// welcome page carries no such field, and that demo stays interactive.
export const selectPageIsMine = (id) => (state) =>
  state.pages?.byIds?.[id]?.is_mine !== false

export const selectPageOwnerLogin = (id) => (state) =>
  state.pages?.byIds?.[id]?.owner_login

// The list is in two groups: my own pages, and the ones other people shared with
// me. Memoised, so a component gets the same array until the pages change.
export const selectMyPageIds = createSelector(
  [(state) => state.pages?.allIds, (state) => state.pages?.byIds],
  (allIds, byIds) =>
    (allIds || []).filter((pageId) => byIds?.[pageId]?.is_mine !== false)
)

export const selectSharedWithMePageIds = createSelector(
  [(state) => state.pages?.allIds, (state) => state.pages?.byIds],
  (allIds, byIds) =>
    (allIds || []).filter((pageId) => byIds?.[pageId]?.is_mine === false)
)

export const selectPagesIsFetching = (state) => state.pages.isFetching

export const selectPageExists = (id) => (state) =>
  (state.pages?.allIds || EMPTY_PAGE_IDS).includes(id)

export const selectPageStikisLoaded = (id) => (state) =>
  (state.pages?.stikisLoadedIds || EMPTY_PAGE_IDS).includes(id)

export const selectMinPageId = (state) => {
  const allIds = state.pages?.allIds || EMPTY_PAGE_IDS
  return allIds.length > 0 ? Math.min(...allIds) : 0
}

export const selectNextAvailableTitle = (candidateTitle) => (state) => {
  const allIds = state.pages?.allIds || EMPTY_PAGE_IDS
  const byIds = state.pages?.byIds || EMPTY_PAGE_MAP
  const likeTitles = allIds.map(
    (pageId) => byIds[pageId]?.title
  ).filter((title) => typeof title === 'string')
    .filter((title) => title.match(new RegExp(`^${candidateTitle}`)))

  let availableTitle = candidateTitle
  for (let i = 0; i < likeTitles.length; i++) {
    if (!likeTitles.includes(availableTitle)) {
      return availableTitle;
    }
    availableTitle = `${candidateTitle} ${i + 1}`
  }
  // At the end of this, availableTitle is guaranteed to be free
  return availableTitle
}
